import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import InformationsPersonnelles from '../steps/InformationsPersonnelles';
import InformationsApicolesADAR from './InformationsApicolesADAR';
import VerificationADAR from './VerificationADAR';
import PaiementADAR from './PaiementADAR';
import RecapitulatifADAR from './RecapitulatifADAR';
import Authentication from '../steps/Authentication';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const getSteps = (user, isEligible) => [
  'Informations personnelles',
  'Informations apicoles',
  'Vérification',
  ...(isEligible ? ['Paiement'] : []),
  ...(isEligible && !user ? ['Création de compte'] : []),
  ...(isEligible ? ['Récapitulatif'] : [])
];

const getInitialValues = (annee) => ({
  organisme: 'ADAR',
  annee: annee || new Date().getFullYear(),
  informationsPersonnelles: {
    typePersonne: 'personne_physique',
    designation: '',
    raisonSociale: '',
    nom: '',
    prenom: '',
    dateNaissance: null,
    adresse: {
      rue: '',
      codePostal: '',
      ville: ''
    },
    telephone: '',
    telephoneMobile: '',
    email: ''
  },
  informationsApicoles: {
    nombreRuches: '',
    nombreRuchers: '',
    localisation: {
      departement: '',
      commune: ''
    },
    napi: '',
    numeroAmexa: '',
    siret: ''
  },
  informationsSpecifiques: {
    ADAR: {}
  },
  documentsADAR: [],
  paiement: {
    typePaiement: 'en_ligne',
    montant: 0
  },
  auth: {
    password: '',
    confirmPassword: ''
  },
  acceptStatutsADAR: false,
  acceptReglementADAR: false,
  acceptConditions: false,
  signature: ''
});

const validationSchema = (user) => Yup.object().shape({
  annee: Yup.number()
    .required('Veuillez sélectionner une année')
    .min(new Date().getFullYear(), 'L\'année doit être l\'année en cours ou future'),
  informationsPersonnelles: Yup.object().shape({
    designation: Yup.string().required('La civilité est requise'),
    nom: Yup.string().required('Le nom est requis'),
    prenom: Yup.string().required('Le prénom est requis'),
    dateNaissance: Yup.date().required('La date de naissance est requise'),
    adresse: Yup.object().shape({
      rue: Yup.string().required('L\'adresse est requise'),
      codePostal: Yup.string().required('Le code postal est requis'),
      ville: Yup.string().required('La ville est requise')
    }),
    telephone: Yup.string(),
    telephoneMobile: Yup.string().required('Le téléphone mobile est requis'),
    email: Yup.string().email('Email invalide').required('L\'email est requis')
  }),
  informationsApicoles: Yup.object().shape({
    nombreRuches: Yup.number()
      .required('Le nombre de ruches est requis')
      .min(1, 'Le nombre de ruches doit être supérieur à 0'),
    nombreRuchers: Yup.number().required('Le nombre d\'emplacements est requis'),
    localisation: Yup.object().shape({
      departement: Yup.string().required('Le département est requis'),
      commune: Yup.string().required('La commune est requise')
    }),
    napi: Yup.string().required('Le NAPI est obligatoire pour adhérer à l\'ADAR'),
    numeroAmexa: Yup.string().when('nombreRuches', ([nombreRuches], schema) => {
      return nombreRuches > 60 
        ? schema.required('Le numéro AMEXA est obligatoire pour plus de 60 ruches')
        : schema;
    }),
    siret: Yup.string()
      .required('Le SIRET est obligatoire pour adhérer à l\'ADAR')
      .matches(/^\d{14}$/, 'Le SIRET doit contenir exactement 14 chiffres')
  }),
  paiement: Yup.object().shape({
    typePaiement: Yup.string().required('Le mode de paiement est requis')
  }),
  auth: user ? Yup.object() : Yup.object().shape({
    password: Yup.string()
      .required('Le mot de passe est requis')
      .min(8, 'Le mot de passe doit contenir au moins 8 caractères')
      .matches(/[A-Z]/, 'Le mot de passe doit contenir au moins une majuscule')
      .matches(/[a-z]/, 'Le mot de passe doit contenir au moins une minuscule')
      .matches(/[0-9]/, 'Le mot de passe doit contenir au moins un chiffre')
      .matches(/[!@#$%^&*(),.?":{}|<>_\-+=[\]\\;'/`~]/, 'Le mot de passe doit contenir au moins un caractère spécial'),
    confirmPassword: Yup.string()
      .required('La confirmation du mot de passe est requise')
      .oneOf([Yup.ref('password')], 'Les mots de passe ne correspondent pas')
  }),
  acceptStatutsADAR: Yup.boolean()
    .oneOf([true], 'Vous devez accepter les statuts de l\'ADAR')
    .required('Vous devez accepter les statuts de l\'ADAR'),
  acceptReglementADAR: Yup.boolean()
    .oneOf([true], 'Vous devez accepter le règlement intérieur de l\'ADAR')
    .required('Vous devez accepter le règlement intérieur de l\'ADAR'),
  acceptConditions: Yup.boolean()
    .oneOf([true], 'Vous devez accepter les conditions générales du site')
    .required('Vous devez accepter les conditions générales du site'),
  signature: Yup.string().required('La signature numérique est obligatoire')
});

export default function AdhesionFormADAR({ annee }) {
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [stepError, setStepError] = useState('');
  const [userPersonalInfo, setUserPersonalInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailExistsError, setEmailExistsError] = useState(false);
  const [isEligible, setIsEligible] = useState(true);
  const { user, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      fetchUserData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const profileResponse = await axios.get(`${process.env.REACT_APP_API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const combinedInfo = {
        typePersonne: profileResponse.data.personalInfo?.typePersonne || profileResponse.data.typePersonne || profileResponse.data.type || '',
        raisonSociale: profileResponse.data.personalInfo?.raisonSociale || profileResponse.data.raisonSociale || '',
        designation: profileResponse.data.personalInfo?.designation || profileResponse.data.designation || '',
        nom: profileResponse.data.personalInfo?.nom || profileResponse.data.nom || '',
        prenom: profileResponse.data.personalInfo?.prenom || profileResponse.data.prenom || '',
        email: profileResponse.data.personalInfo?.email || profileResponse.data.email || '',
        telephone: profileResponse.data.personalInfo?.telephone || profileResponse.data.telephone || '',
        telephoneMobile: profileResponse.data.personalInfo?.telephoneMobile || profileResponse.data.telephoneMobile || '',
        dateNaissance: profileResponse.data.personalInfo?.dateNaissance || profileResponse.data.dateNaissance || null,
        adresse: profileResponse.data.personalInfo?.adresse || profileResponse.data.adresse || { rue: '', codePostal: '', ville: '' },
        napi: profileResponse.data.napi || '',
        numeroAmexa: profileResponse.data.numeroAmexa || '',
        siret: profileResponse.data.siret || ''
      };
      
      setUserPersonalInfo(combinedInfo);
    } catch (error) {
      console.error('Erreur lors du chargement des données utilisateur:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateCurrentStep = async (formikProps, stepName) => {
    const { setTouched, validateForm, values } = formikProps;
    
    const stepFieldsMap = {
      'Informations personnelles': [
        'informationsPersonnelles.designation',
        'informationsPersonnelles.nom',
        'informationsPersonnelles.prenom',
        'informationsPersonnelles.dateNaissance',
        'informationsPersonnelles.adresse.rue',
        'informationsPersonnelles.adresse.codePostal',
        'informationsPersonnelles.adresse.ville',
        'informationsPersonnelles.telephoneMobile',
        'informationsPersonnelles.email'
      ],
      'Informations apicoles': [
        'informationsApicoles.nombreRuches',
        'informationsApicoles.nombreRuchers',
        'informationsApicoles.localisation.departement',
        'informationsApicoles.localisation.commune',
        'informationsApicoles.napi',
        'informationsApicoles.siret'
      ],
      'Paiement': ['paiement.typePaiement'],
      'Création de compte': ['auth.password', 'auth.confirmPassword'],
      'Récapitulatif': ['acceptConditions', 'acceptStatutsADAR', 'acceptReglementADAR', 'signature']
    };

    // Validation spécifique pour Informations apicoles (documents)
    if (stepName === 'Informations apicoles') {
      const documents = values.documentsADAR || [];
      const nombreRuches = values.informationsApicoles?.nombreRuches || 0;
      const hasDeclarationRuches = documents.some(doc => doc.type === 'declaration_ruches');
      
      if (!hasDeclarationRuches) {
        setStepError('Veuillez télécharger votre déclaration de ruches pour continuer');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return false;
      }
      
      // Vérifier le relevé CGSS si plus de 60 ruches ET si ce n'est pas une association ou établissement public
      const typePersonne = values.informationsPersonnelles?.typePersonne || '';
      const isExemptFromCGSS = typePersonne === 'association' || typePersonne === 'etablissement_public';
      
      if (nombreRuches >= 60 && !isExemptFromCGSS) {
        const hasReleveCGSS = documents.some(doc => doc.type === 'releve_exploitation_cgss');
        if (!hasReleveCGSS) {
          setStepError('Veuillez télécharger le relevé d\'exploitation CGSS pour continuer (obligatoire pour plus de 60 ruches)');
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return false;
        }
      }
    }

    const errors = await validateForm();
    const fieldsToValidate = stepFieldsMap[stepName] || [];
    
    const touchedFields = {};
    fieldsToValidate.forEach(field => {
      const parts = field.split('.');
      let current = touchedFields;
      parts.forEach((part, index) => {
        if (index === parts.length - 1) {
          current[part] = true;
        } else {
          current[part] = current[part] || {};
          current = current[part];
        }
      });
    });
    setTouched(touchedFields);

    let hasErrors = false;
    fieldsToValidate.forEach(field => {
      const parts = field.split('.');
      let errorValue = errors;
      for (const part of parts) {
        if (errorValue && errorValue[part] !== undefined) {
          errorValue = errorValue[part];
        } else {
          errorValue = null;
          break;
        }
      }
      if (errorValue) {
        hasErrors = true;
      }
    });

    return !hasErrors;
  };

  const handleNext = async (formikProps) => {
    const steps = getSteps(user, isEligible);
    const currentStepName = steps[activeStep];
    
    // Vérification spéciale pour l'étape de vérification
    if (currentStepName === 'Vérification') {
      // Vérifier l'éligibilité
      const nombreRuches = formikProps.values.informationsApicoles?.nombreRuches || 0;
      
      // Vérifier si primo-adhérent
      let isPrimoAdherent = true;
      if (user && annee) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/adhesions/user`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          console.log('Toutes les adhésions de l\'utilisateur:', response.data);
          console.log('Année recherchée (N-1):', annee - 1);
          console.log('Adhésions ADAR:', response.data.filter(adh => adh.organisme === 'ADAR'));
          
          const adhesionN1 = response.data.find(
            adh => adh.organisme === 'ADAR' && 
                   adh.annee === annee - 1 && 
                   (adh.status === 'actif' || adh.status === 'expiree')
          );
          isPrimoAdherent = !adhesionN1;
          console.log('adhesionN1 trouvée:', adhesionN1);
          console.log('isPrimoAdherent:', isPrimoAdherent);
        } catch (error) {
          console.error('Erreur vérification adhésion N-1:', error);
        }
      }
      
      // Si primo-adhérent et moins de 30 ruches : bloquer
      if (isPrimoAdherent && nombreRuches < 30) {
        setIsEligible(false);
        setStepError('');
        // Ne pas avancer, rester sur l'étape de vérification
        return;
      } else {
        setIsEligible(true);
      }
    }
    
    const isValid = await validateCurrentStep(formikProps, currentStepName);
    
    if (isValid) {
      // Vérifier l'email si étape informations personnelles et utilisateur non connecté
      if (currentStepName === 'Informations personnelles' && !user) {
        try {
          const email = formikProps.values.informationsPersonnelles.email;
          const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/check-email`, { email });
          
          if (response.data.exists) {
            setEmailExistsError(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
          }
        } catch (error) {
          console.error('Erreur lors de la vérification de l\'email:', error);
        }
      }
      
      setStepError('');
      setEmailExistsError(false);
      setActiveStep((prevStep) => prevStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      if (currentStepName === 'Récapitulatif') {
        setStepError('Veuillez signer électroniquement votre demande d\'adhésion avant de soumettre.');
      } else {
        setStepError('Veuillez remplir tous les champs obligatoires avant de continuer.');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    const steps = getSteps(user, isEligible);
    const currentStepName = steps[activeStep];
    
    // Si on quitte l'étape de vérification, réinitialiser l'éligibilité
    if (currentStepName === 'Vérification') {
      setIsEligible(true);
    }
    
    setStepError('');
    setEmailExistsError(false);
    setActiveStep((prevStep) => prevStep - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLoginRedirect = () => {
    navigate('/login');
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitError('');
      setIsSubmitting(true);
      
      let authToken;
      if (!user) {
        const registerResponse = await axios.post(`${process.env.REACT_APP_API_URL}/auth/register`, {
          type: values.informationsPersonnelles.type,
          raisonSociale: values.informationsPersonnelles.raisonSociale,
          designation: values.informationsPersonnelles.designation,
          email: values.informationsPersonnelles.email,
          password: values.auth.password,
          prenom: values.informationsPersonnelles.prenom,
          nom: values.informationsPersonnelles.nom,
          telephone: values.informationsPersonnelles.telephone,
          telephoneMobile: values.informationsPersonnelles.telephoneMobile,
          adresse: values.informationsPersonnelles.adresse,
          dateNaissance: values.informationsPersonnelles.dateNaissance
        });
        
        authToken = registerResponse.data.token;
        localStorage.setItem('token', authToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
        await login(values.informationsPersonnelles.email, values.auth.password);
      } else {
        authToken = localStorage.getItem('token');
      }

      const adhesionData = {
        organisme: 'ADAR',
        annee: values.annee,
        informationsApicoles: values.informationsApicoles,
        informationsPersonnelles: values.informationsPersonnelles,
        informationsSpecifiques: values.informationsSpecifiques,
        paiement: values.paiement,
        signature: values.signature,
        documents: values.documentsADAR || []
      };
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/adhesions`,
        adhesionData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (response.data) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      setSubmitError(
        error.response?.data?.message ||
        'Une erreur est survenue lors de la validation de votre adhésion.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = (step, formikProps) => {
    const steps = getSteps(user, isEligible);
    const stepName = steps[step];

    switch (stepName) {
      case 'Informations personnelles':
        return <InformationsPersonnelles formikProps={formikProps} user={userPersonalInfo} />;
      case 'Informations apicoles':
        return <InformationsApicolesADAR formikProps={formikProps} />;
      case 'Vérification':
        return <VerificationADAR formikProps={formikProps} />;
      case 'Paiement':
        return <PaiementADAR formikProps={formikProps} />;
      case 'Création de compte':
        return <Authentication formikProps={formikProps} />;
      case 'Récapitulatif':
        return <RecapitulatifADAR formikProps={formikProps} />;
      default:
        return null;
    }
  };

  const getInitialFormValues = () => {
    const baseValues = getInitialValues(annee);
    
    if (user && userPersonalInfo) {
      return {
        ...baseValues,
        informationsPersonnelles: {
          typePersonne: userPersonalInfo.typePersonne || 'personne_physique',
          designation: userPersonalInfo.designation || '',
          raisonSociale: userPersonalInfo.raisonSociale || '',
          nom: userPersonalInfo.nom || '',
          prenom: userPersonalInfo.prenom || '',
          dateNaissance: userPersonalInfo.dateNaissance ? new Date(userPersonalInfo.dateNaissance) : null,
          adresse: {
            rue: userPersonalInfo.adresse?.rue || '',
            codePostal: userPersonalInfo.adresse?.codePostal || '',
            ville: userPersonalInfo.adresse?.ville || ''
          },
          telephone: userPersonalInfo.telephone || '',
          telephoneMobile: userPersonalInfo.telephoneMobile || '',
          email: userPersonalInfo.email || ''
        },
        informationsApicoles: {
          ...baseValues.informationsApicoles,
          napi: userPersonalInfo.napi || '',
          numeroAmexa: userPersonalInfo.numeroAmexa || '',
          siret: userPersonalInfo.siret || ''
        }
      };
    }
    return baseValues;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-gray-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 pb-8">
      <div className="mb-8">
        <div className="flex items-center gap-4 justify-center">
          {getSteps(user, isEligible).map((label, index) => (
            <div key={label} className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 
                ${index <= activeStep ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-300 text-gray-300'}`}>
                {index + 1}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Formik
        initialValues={getInitialFormValues()}
        validationSchema={validationSchema(user)}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formikProps) => {
          const steps = getSteps(user, isEligible);
          const isLastStep = activeStep === steps.length - 1;
          const hasValidationErrors =
            Object.keys(formikProps.errors || {}).length > 0 &&
            formikProps.submitCount > 0;

          return (
            <Form>
              {renderStepContent(activeStep, formikProps)}

              {emailExistsError && (
                <div className="mt-4 rounded-md bg-honey-50 p-6 border border-honey-200">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-honey-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="text-sm font-medium text-honey-800">Compte existant</h3>
                      <div className="mt-2 text-sm text-honey-700">
                        <p>Un compte associé à cette adresse email existe déjà. Veuillez vous connecter pour continuer votre adhésion.</p>
                      </div>
                      <div className="mt-4">
                        <button
                          type="button"
                          onClick={handleLoginRedirect}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                        >
                          Se connecter
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {stepError && (
                <div className="mt-4 rounded-md bg-red-50 p-4 border border-red-200">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700 font-medium">{stepError}</p>
                    </div>
                  </div>
                </div>
              )}

              {isLastStep && hasValidationErrors && (
                <div className="mt-4 rounded-md bg-yellow-50 p-4">
                  <p className="text-sm text-yellow-800">
                    Certaines informations sont manquantes ou invalides. Merci de vérifier les différentes étapes avant de soumettre.
                  </p>
                </div>
              )}

              {submitError && (
                <div className="mt-4 rounded-md bg-red-50 p-4">
                  <p className="text-sm text-red-700">{submitError}</p>
                </div>
              )}

              <div className="mt-8 flex justify-between">
                {activeStep > 0 && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Précédent
                  </button>
                )}
                <div className="flex-1" />
                {activeStep < steps.length - 1 ? (
                  <button
                    type="button"
                    onClick={() => handleNext(formikProps)}
                    disabled={!isEligible && steps[activeStep] === 'Vérification'}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {!isEligible && steps[activeStep] === 'Vérification' ? 'Non éligible' : 'Suivant'}
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting || !isEligible}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Envoi en cours...' : !isEligible ? 'Adhésion refusée' : 'Soumettre'}
                  </button>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
